import { useState, useEffect } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const statusColor = (s) => {
  const map = {
    Active: "bg-emerald-500/15 text-emerald-500 border-emerald-500/20",
    Expired: "bg-red-500/15 text-red-500 border-red-500/20",
    Partial: "bg-amber-500/15 text-amber-500 border-amber-500/20",
    Pending: "bg-blue-500/15 text-blue-500 border-blue-500/20",
    Inactive: "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
  };
  return map[s] || "bg-zinc-500/15 text-zinc-400 border-zinc-500/20";
};

export function QuickAttendanceDialog({ open, onOpenChange, member, branchId, onSuccess }) {
  const [date, setDate] = useState("");
  const [checkInTime, setCheckInTime] = useState("");
  const [checkOutTime, setCheckOutTime] = useState("");
  const [attendanceList, setAttendanceList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const fetchMemberAttendance = async (memberId) => {
    setListLoading(true);
    try {
      const res = await api.get(`/attendance/member/${memberId}`);
      setAttendanceList(res.data.attendance || []);
    } catch (err) {
      toast.error("Failed to load attendance history");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (open && member) {
      const now = new Date();
      setDate(now.toLocaleDateString("sv-SE"));
      setCheckInTime(now.toTimeString().slice(0, 5));
      setCheckOutTime("");
      fetchMemberAttendance(member.id);
    }
  }, [open, member]);

  if (!member) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const checkInIso = `${date}T${checkInTime}:00`;
      const checkOutIso = checkOutTime ? `${date}T${checkOutTime}:00` : null;

      await api.post("/attendance/checkin", {
        memberId: member.id,
        branchId: branchId || "",
        checkIn: checkInIso,
        checkOut: checkOutIso,
        date: date
      });

      toast.success("Attendance logged successfully");
      fetchMemberAttendance(member.id);
      setCheckOutTime("");
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to log attendance");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-2">
          <DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" /> Log Member Attendance
          </DialogTitle>
        </DialogHeader>

        {/* Member small section */}
        <div className="flex items-center gap-3 p-3 bg-secondary/20 border border-border/50 rounded-xl mb-4">
          <Avatar className="h-10 w-10">
            {member.photo ? <AvatarImage src={`${BACKEND_URL}${member.photo}`} /> : null}
            <AvatarFallback className="bg-primary/10 text-primary text-sm font-bold">
              {member.name?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-sm leading-tight text-foreground truncate">{member.name}</p>
            <p className="text-xs text-muted-foreground font-mono mt-0.5">{member.phone || "No phone"}</p>
          </div>
          {(member.membership?.status || member.status) && (
            <Badge className={`${statusColor(member.membership?.status || member.status || "New")} border text-[10px] font-mono uppercase px-1.5 py-0`}>
              {member.membership?.status || member.status}
            </Badge>
          )}
        </div>

        {/* Log Attendance Form */}
        <form onSubmit={handleSubmit} className="space-y-4 border-b border-border pb-4 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label htmlFor="att-date" className="text-xs font-heading uppercase tracking-wider text-muted-foreground">Date</Label>
              <Input
                id="att-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="h-9 text-xs"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="att-checkin" className="text-xs font-heading uppercase tracking-wider text-muted-foreground">Check-in Time</Label>
              <Input
                id="att-checkin"
                type="time"
                value={checkInTime}
                onChange={(e) => setCheckInTime(e.target.value)}
                required
                className="h-9 text-xs"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="att-checkout" className="text-xs font-heading uppercase tracking-wider text-muted-foreground">Check-out Time</Label>
              <Input
                id="att-checkout"
                type="time"
                value={checkOutTime}
                onChange={(e) => setCheckOutTime(e.target.value)}
                className="h-9 text-xs"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="submit" size="sm" className="h-9 text-xs font-heading uppercase tracking-wide gap-1" disabled={loading}>
              {loading ? "Logging..." : "Log Attendance"}
            </Button>
          </div>
        </form>

        {/* Recent Attendance list */}
        <div className="space-y-2">
          <h4 className="text-xs font-heading uppercase tracking-wider text-muted-foreground">Recent Attendance History</h4>
          {listLoading ? (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
            </div>
          ) : attendanceList.length === 0 ? (
            <p className="text-center py-4 text-xs text-muted-foreground">No attendance records found.</p>
          ) : (
            <div className="border border-border rounded-xl overflow-hidden max-h-60 overflow-y-auto">
              <Table>
                <TableHeader className="bg-secondary/10 sticky top-0 z-10">
                  <TableRow>
                    <TableHead className="text-[10px] uppercase font-mono h-8 px-3">Date</TableHead>
                    <TableHead className="text-[10px] uppercase font-mono h-8 px-3">In</TableHead>
                    <TableHead className="text-[10px] uppercase font-mono h-8 px-3">Out</TableHead>
                    <TableHead className="text-[10px] uppercase font-mono h-8 px-3">Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...attendanceList]
                    .sort((x, y) => new Date(y.checkIn || y.date) - new Date(x.checkIn || x.date))
                    .slice(0, 10)
                    .map((a) => (
                      <TableRow key={a.id} className="hover:bg-secondary/5">
                        <TableCell className="font-mono text-xs py-2 px-3">{a.date?.slice(0, 10)}</TableCell>
                        <TableCell className="font-mono text-xs py-2 px-3">
                          {a.checkIn ? new Date(a.checkIn).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "-"}
                        </TableCell>
                        <TableCell className="font-mono text-xs py-2 px-3">
                          {a.checkOut ? new Date(a.checkOut).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "-"}
                        </TableCell>
                        <TableCell className="font-mono text-xs py-2 px-3">
                          {(() => {
                            if (a.duration) return `${Math.floor(a.duration / 60)}h ${a.duration % 60}m`;
                            if (!a.checkIn || !a.checkOut) return "-";
                            const diff = new Date(a.checkOut) - new Date(a.checkIn);
                            if (isNaN(diff) || diff < 0) return "-";
                            const mins = Math.floor(diff / 60000);
                            return `${Math.floor(mins / 60)}h ${mins % 60}m`;
                          })()}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
